import React, { useMemo, useState, useEffect } from "react";
import { Video, ExternalLink, Monitor } from "lucide-react";
import { Button } from "../ui/button";

interface SharePointVideoPlayerProps {
  videoEmbed: string;
  title?: string;
}

/**
 * Detect mobile/tablet devices where the SharePoint/Stream embedded player
 * adds an intrusive Microsoft watermark. We treat touch-capable devices with
 * a mobile-ish viewport or mobile UA as "mobile".
 */
function isMobileDevice(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return false;
  }
  const ua = navigator.userAgent.toLowerCase();
  const isMobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i.test(ua);
  const isTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
  return isMobileUA || (isTouch && window.innerWidth < 1024);
}

function extractIframeSrc(html: string): string | null {
  if (!html) return null;
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const iframe = doc.querySelector("iframe");
    const src = iframe?.getAttribute("src");
    if (src) return src;
  } catch {
    // Fall back to regex parsing
  }
  const match = html.match(/src=["']([^"']+)["']/i);
  return match ? match[1] : null;
}

function normalizeEmbedHtml(html: string): string {
  if (!html) return "";
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const iframe = doc.querySelector("iframe");
    if (!iframe) return html;

    // Make the iframe fully responsive.
    iframe.setAttribute("width", "100%");
    iframe.setAttribute("height", "100%");
    iframe.setAttribute("frameborder", "0");
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute(
      "allow",
      "autoplay; fullscreen; encrypted-media; picture-in-picture; web-share"
    );
    iframe.setAttribute("loading", "lazy");
    iframe.setAttribute("title", iframe.getAttribute("title") || "Procedure video");
    iframe.style.width = "100%";
    iframe.style.height = "100%";
    iframe.style.border = "0";

    return iframe.outerHTML;
  } catch {
    return html;
  }
}

export default function SharePointVideoPlayer({
  videoEmbed,
  title = "Procedure Video",
}: SharePointVideoPlayerProps) {
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    setMobile(isMobileDevice());
    const handleResize = () => setMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const videoUrl = useMemo(() => extractIframeSrc(videoEmbed), [videoEmbed]);
  const embedHtml = useMemo(() => normalizeEmbedHtml(videoEmbed), [videoEmbed]);

  // On mobile we avoid the Microsoft watermark by not embedding the iframe
  // inline. Instead we show a launcher that opens the SharePoint video URL
  // directly in the browser / Stream app.
  // if (mobile) {
  //   return (
  //     <div
  //       className="relative flex flex-col items-center justify-center rounded-lg overflow-hidden bg-gradient-to-br from-slate-900 to-slate-800 text-white py-8"
  //       style={{ aspectRatio: "16/9" }}
  //     >
  //       <div className="rounded-full bg-white/10 p-4 mb-3">
  //         <Video className="w-10 h-10" />
  //       </div>
  //       <p className="text-base font-medium px-4 text-center">{title}</p>
  //       <p className="text-xs text-white/70 mt-1 px-6 text-center">
  //         Tap below to watch the video in your browser.
  //       </p>

  //       {videoUrl ? (
  //         <Button
  //           size="sm"
  //           variant="secondary"
  //           className="mt-4 gap-2"
  //           asChild
  //         >
  //           <a
  //             href={videoUrl}
  //             target="_blank"
  //             rel="noopener noreferrer"
  //           >
  //             <ExternalLink className="w-4 h-4" />
  //             Watch Video
  //           </a>
  //         </Button>
  //       ) : (
  //         <p className="text-xs text-red-300 mt-4 px-4 text-center">
  //           Could not read the SharePoint video URL.
  //         </p>
  //       )}
  //     </div>
  //   );
  // }

  return (
    <div
      className="w-full rounded-lg overflow-hidden bg-black"
      style={{ aspectRatio: "16/9" }}
    >
      {embedHtml ? (
        <div
          className="w-full h-full"
          dangerouslySetInnerHTML={{ __html: embedHtml }}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-white gap-2">
          <Monitor className="w-8 h-8 opacity-70" />
          <p className="text-sm opacity-80">Unable to load video embed</p>
        </div>
      )}
    </div>
  );
}
